import React from "react";
import { computeAspectRatio, getAspectRatio } from "../../../common/lib/aspect-ratio";
import { getImageSource } from "../../../common/lib/image-source";
import type { Image } from "../types";
import { useAdImageOptions } from "./AdImageOptions";

const classes = {
	image: "w-full h-full object-center",
	contain: "object-contain",
	crop: "object-cover",
	fallback: "object-none",
};

export type AdImageProps = {
	adaptiveAspect?: boolean;
	alt?: string;
	aspectClass?: string;
	children?: ImageRenderProp;
	crop?: boolean;
	imageClass?: string;
	preserveAspect?: boolean;
	sizes?: string;
} & Partial<Image>;

type ImageRenderProp = (args: {
	className?: string;
	src?: string;
	srcSet?: string;
	sizes?: string;
}) => React.ReactElement | null;

export function AdImage(props: AdImageProps) {
	const {
		adaptiveAspect = false,
		alt = "",
		aspectClass = "",
		children,
		crop = false,
		imageClass = "",
		preserveAspect = false,
		sizes,
		...imageProps
	} = props;

	const { aspect_ratio, height, url, width } = imageProps;
	const { fallbackImage, ...imageSourceOptions } = useAdImageOptions();

	function getValidatedImageSource() {
		const { src, srcSet } = getImageSource(
			{
				url,
				width,
			},
			imageSourceOptions,
		);

		const hasValidImageSource = Boolean(srcSet) && Boolean(sizes);
		if (hasValidImageSource) {
			return { src, srcSet, sizes };
		} else {
			return { src };
		}
	}

	function getAspectRatioStyles() {
		const ratio = computeAspectRatio(aspect_ratio, height, width);
		const hasSpecificAspectRatio = ratio !== null && ratio > 0;
		return hasSpecificAspectRatio
			? {
					className: "aspect-1/1",
					style: { paddingBottom: `${(1 / ratio) * 100}%` },
				}
			: {
					className: aspectClass,
				};
	}

	function getAspectRatioClasses() {
		return adaptiveAspect
			? getAspectRatio({
					aspect_ratio,
					height,
					width,
				})
			: aspectClass;
	}

	function getImageClasses({ isFallbackImage = false }) {
		if (isFallbackImage) {
			return `${classes.image} ${classes.fallback} ${imageClass}`;
		}

		return `${classes.image} ${crop ? classes.crop : classes.contain} ${imageClass}`;
	}

	function wrapImage(imageEle: React.ReactElement | null) {
		let aspectRatioStyles;
		if (preserveAspect) {
			aspectRatioStyles = getAspectRatioStyles();
		} else if (adaptiveAspect) {
			aspectRatioStyles = { className: getAspectRatioClasses() };
		} else {
			aspectRatioStyles = { className: aspectClass };
		}

		return (
			<div className={aspectRatioStyles.className} style={aspectRatioStyles.style}>
				{imageEle}
			</div>
		);
	}

	const imageSource = getValidatedImageSource();
	const isFallbackImage = !imageSource.src;

	const imageEleProps = {
		className: getImageClasses({
			isFallbackImage,
		}),
		sizes: imageSource.sizes,
		src: imageSource.src,
		srcSet: imageSource.srcSet,
	};

	if (isFallbackImage) {
		return wrapImage(fallbackImage(imageEleProps));
	}

	const hasRenderProp = typeof children === "function";
	return hasRenderProp ? wrapImage(children(imageEleProps)) : wrapImage(<img alt={alt} {...imageEleProps} />);
}
